import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  alpha,
  Card,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import dayjs from "dayjs";
import "dayjs/locale/he";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import { protectedResources } from "../../authConfig";
import { CATEGORICAL_COLORS, CHART_INK, formatMonthLabel } from "../../components/StatisticsDashboard/chartUtils";
import NotFoundPage from "../ErrorPage";

const BASE_URL = (process.env.REACT_APP_BACKEND_BASE_URL || "").trim();
const PAGE_COLOR = "#b45309";
const TREND_CHART_COLOR = CATEGORICAL_COLORS[0];
const LABEL_PRINTS_CHART_COLOR = CATEGORICAL_COLORS[2];

// How many weeks are shown per page when the trend chart is in week view.
const WEEK_WINDOW_SIZE = 12;

// Entry to /EldanDashboard only requires the "ManagersDashboardPage" role
// permission (checked by ProtectedRoute). Whether this specific sub-dashboard
// is enabled is a separate, narrower grant living in managerDashboardPermissionModel
const ELDAN_DASHBOARD_KEY = "eldandashboard";

const GRANULARITY_OPTIONS = [
  { value: "day", label: "יומי" },
  { value: "week", label: "שבועי" },
  { value: "month", label: "חודשי" },
];

// `label` is always the ISO start-date of the bucket (see eldanDashboardService.js).
const formatTrendLabel = (label, granularity) => {
  if (!label) return "";
  const [year, month, day] = label.split("-").map(Number);
  if (granularity === "month") {
    return formatMonthLabel(year, month);
  }
  const start = new Date(year, month - 1, day);
  const formatDMY = (d) => d.toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit" });
  if (granularity === "week") {
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return `${formatDMY(start)} - ${formatDMY(end)}`;
  }
  return formatDMY(start);
};

const SENT_TO_PRIORITY_STATUS = "send to priorty";
const SENT_TO_PRIORITY_LABEL = "סה״כ קריאות שנשלחו לפריוריטי עד היום";

// Recharts renders its Y-axis category labels as plain SVG <text>, and under
// this app's RTL theme the browser reinterprets `text-anchor="end"` relative
// to the surrounding bidi context — instead of ending at the axis line and
// growing left (into the reserved label strip), the text starts at the axis
// line and grows right, straight over the bar. A wrapping `direction: ltr`
// isn't enough to stop that reinterpretation, so this tick renders its own
// <text> with an explicit bidi override, forcing the anchor to behave the
// same regardless of the page's direction.
const UsernameAxisTick = ({ x, y, payload }) => (
  <text
    x={x}
    y={y}
    dy={4}
    textAnchor="end"
    style={{ direction: "ltr", unicodeBidi: "bidi-override" }}
    fontSize={11}
    fill={CHART_INK.muted}
  >
    {payload.value}
  </text>
);

const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card sx={{ p: 2.5, borderRadius: 3, display: "flex", alignItems: "center", gap: 2, height: "100%" }}>
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: "50%",
        bgcolor: alpha(color, 0.12),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon sx={{ color, fontSize: 26 }} />
    </Box>
    <Box>
      <Typography variant="h5" fontWeight={700}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Box>
  </Card>
);

const EldanDashboardPage = () => {
  // "checking" | "granted" | "denied"
  const [dashboardAccess, setDashboardAccess] = useState("checking");
  const [granularity, setGranularity] = useState("day");
  const [trendMonthAnchor, setTrendMonthAnchor] = useState(() => dayjs().startOf("month"));
  const [trendYearAnchor, setTrendYearAnchor] = useState(() => dayjs().startOf("year"));
  const [trendWeekAnchor, setTrendWeekAnchor] = useState(() => dayjs().startOf("day"));
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [labelPrintsPerUser, setLabelPrintsPerUser] = useState([]);
  const [tickets, setTickets] = useState({ data: [], total: 0, truncated: false });
  const [search, setSearch] = useState("");

  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingTrend, setLoadingTrend] = useState(true);
  const [loadingLabelPrints, setLoadingLabelPrints] = useState(true);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [error, setError] = useState(null);

  const { execute: fetchRead } = useFetchWithMsal({ scopes: protectedResources.apiScopes.scopes.read });

  useEffect(() => {
    let cancelled = false;

    // A definitive 403 means the backend evaluated access and said no.
    // Anything else (network hiccup, cold-start 5xx, token refresh blip) is a
    // failed check, not a denial — retry once before giving up.
    const checkAccess = async (isRetry = false) => {
      try {
        const result = await fetchRead("GET", `${BASE_URL}/api/managerDashboard/myAccess`);
        if (cancelled) return;
        const allowed = !!result?.isAdmin || !!result?.allowedDashboards?.includes(ELDAN_DASHBOARD_KEY);
        setDashboardAccess(allowed ? "granted" : "denied");
      } catch (err) {
        if (cancelled) return;
        if (err?.status === 403 || isRetry) {
          setDashboardAccess("denied");
          return;
        }
        setTimeout(() => checkAccess(true), 500);
      }
    };

    checkAccess();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // No date scoping — this is always the all-time total sent to priority as of now.
  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const res = await fetchRead("GET", `${BASE_URL}/api/eldanDashboard/summary`);
      setSummary(res);
    } catch (err) {
      setError(err?.msg || "שגיאה בטעינת נתוני הדשבורד");
    } finally {
      setLoadingSummary(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The trend chart navigates its own window (day view -> by month, week view
  // -> by a block of WEEK_WINDOW_SIZE weeks, month view -> by year).
  const trendDateRange = useMemo(() => {
    const today = dayjs();
    if (granularity === "month") {
      const start = trendYearAnchor.startOf("year");
      const end = trendYearAnchor.isSame(today, "year") ? today : trendYearAnchor.endOf("year");
      return { startDate: start.format("YYYY-MM-DD"), endDate: end.format("YYYY-MM-DD") };
    }
    if (granularity === "week") {
      const end = trendWeekAnchor.isAfter(today, "day") ? today : trendWeekAnchor;
      const start = end.subtract(WEEK_WINDOW_SIZE - 1, "week").startOf("week");
      return { startDate: start.format("YYYY-MM-DD"), endDate: end.format("YYYY-MM-DD") };
    }
    const start = trendMonthAnchor.startOf("month");
    const end = trendMonthAnchor.isSame(today, "month") ? today : trendMonthAnchor.endOf("month");
    return { startDate: start.format("YYYY-MM-DD"), endDate: end.format("YYYY-MM-DD") };
  }, [granularity, trendMonthAnchor, trendYearAnchor, trendWeekAnchor]);

  const loadTrend = useCallback(async () => {
    setLoadingTrend(true);
    try {
      const params = new URLSearchParams({
        startDate: trendDateRange.startDate,
        endDate: trendDateRange.endDate,
        granularity,
      });
      const res = await fetchRead("GET", `${BASE_URL}/api/eldanDashboard/ticketsTrend?${params.toString()}`);
      setTrend(res.data || []);
    } catch (err) {
      setError(err?.msg || "שגיאה בטעינת מגמת הקריאות");
    } finally {
      setLoadingTrend(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendDateRange, granularity]);

  const handleTrendPrev = () => {
    if (granularity === "month") setTrendYearAnchor((prev) => prev.subtract(1, "year"));
    else if (granularity === "week") setTrendWeekAnchor((prev) => prev.subtract(WEEK_WINDOW_SIZE, "week"));
    else setTrendMonthAnchor((prev) => prev.subtract(1, "month"));
  };

  const handleTrendNext = () => {
    if (granularity === "month") setTrendYearAnchor((prev) => prev.add(1, "year"));
    else if (granularity === "week") setTrendWeekAnchor((prev) => prev.add(WEEK_WINDOW_SIZE, "week"));
    else setTrendMonthAnchor((prev) => prev.add(1, "month"));
  };

  const isTrendNextDisabled = granularity === "month"
    ? trendYearAnchor.isSame(dayjs(), "year")
    : granularity === "week"
    ? !trendWeekAnchor.isBefore(dayjs(), "day")
    : trendMonthAnchor.isSame(dayjs(), "month");

  const trendPeriodLabel = granularity === "month"
    ? trendYearAnchor.format("YYYY")
    : granularity === "week"
    ? `${dayjs(trendDateRange.startDate).format("DD/MM/YYYY")} - ${dayjs(trendDateRange.endDate).format("DD/MM/YYYY")}`
    : trendMonthAnchor.locale("he").format("MMMM YYYY");

  // All-time, successful prints only — reads dymo_eldan_label_logs, a
  // separate collection from the eldan_qrcodes tickets above.
  const loadLabelPrintsPerUser = useCallback(async () => {
    setLoadingLabelPrints(true);
    try {
      const res = await fetchRead("GET", `${BASE_URL}/api/eldanDashboard/labelPrintsPerUser`);
      setLabelPrintsPerUser(res.data || []);
    } catch (err) {
      setError(err?.msg || "שגיאה בטעינת נתוני הדפסת המדבקות");
    } finally {
      setLoadingLabelPrints(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTickets = useCallback(async () => {
    setLoadingTickets(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      const query = params.toString();
      const res = await fetchRead("GET", `${BASE_URL}/api/eldanDashboard/tickets${query ? `?${query}` : ""}`);
      setTickets(res);
    } catch (err) {
      setError(err?.msg || "שגיאה בטעינת רשימת הקריאות");
    } finally {
      setLoadingTickets(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadTrend(); }, [loadTrend]);
  useEffect(() => { loadLabelPrintsPerUser(); }, [loadLabelPrintsPerUser]);
  useEffect(() => { loadTickets(); }, [loadTickets]);

  const trendChartData = useMemo(
    () => trend.map((point) => ({ ...point, label: formatTrendLabel(point.label, granularity) })),
    [trend, granularity]
  );

  const sentToPriorityCount = useMemo(
    () => (summary?.byStatus || []).find((entry) => entry.status === SENT_TO_PRIORITY_STATUS)?.count ?? 0,
    [summary]
  );

  if (dashboardAccess === "checking") {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: PAGE_COLOR }} />
      </Box>
    );
  }

  if (dashboardAccess === "denied") {
    return <NotFoundPage />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography variant="h5" fontWeight={700}>אלדן - דשבורד קודי QR</Typography>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Alert severity="info">שימו לב: הנתונים המוצגים במסך זה זמינים החל מחודש אפריל 2026.</Alert>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={8} md={4}>
          <StatCard
            label={SENT_TO_PRIORITY_LABEL}
            value={loadingSummary ? "—" : sentToPriorityCount}
            icon={CheckCircleOutlineIcon}
            color={CATEGORICAL_COLORS[1]}
          />
        </Grid>
      </Grid>

      <Divider />

      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={1.5}>
          <Typography variant="h6" fontWeight={700}>קריאות שנפתחו לאורך זמן</Typography>
          <Box display="flex" alignItems="center" gap={2} flexWrap={{ xs: "wrap", sm: "nowrap" }}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <IconButton size="small" onClick={handleTrendPrev} aria-label="תקופה קודמת">
                <NavigateNextIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 90, textAlign: "center", whiteSpace: "nowrap" }}>
                {trendPeriodLabel}
              </Typography>
              <IconButton size="small" onClick={handleTrendNext} disabled={isTrendNextDisabled} aria-label="תקופה הבאה">
                <NavigateBeforeIcon fontSize="small" />
              </IconButton>
            </Box>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={granularity}
              onChange={(_, value) => value && setGranularity(value)}
            >
              {GRANULARITY_OPTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Box>
        <Card sx={{ p: 2, borderRadius: 3 }}>
          {loadingTrend ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress sx={{ color: TREND_CHART_COLOR }} /></Box>
          ) : trendChartData.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>אין קריאות בטווח שנבחר</Typography>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={trendChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={CHART_INK.grid} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: CHART_INK.muted }} axisLine={{ stroke: CHART_INK.axis }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: CHART_INK.muted }} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
                <Tooltip formatter={(value) => [`${value} קריאות`, ""]} cursor={{ fill: alpha(TREND_CHART_COLOR, 0.06) }} />
                <Bar dataKey="count" name="קריאות" fill={TREND_CHART_COLOR} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </Box>

      <Divider />

      <Box>
        <Typography variant="h6" fontWeight={700} mb={1.5}>הדפסת מדבקות פר משתמש</Typography>
        <Card sx={{ p: 2, borderRadius: 3 }}>
          {loadingLabelPrints ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress sx={{ color: LABEL_PRINTS_CHART_COLOR }} /></Box>
          ) : labelPrintsPerUser.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>אין נתוני הדפסות מוצלחות</Typography>
          ) : (
            // Recharts isn't RTL-aware — under the app's inherited `direction: rtl`,
            // the browser flips how the Y-axis <text> anchors itself, so the
            // usernames render on top of the bars instead of beside them.
            // Forcing LTR on the chart's own wrapper sidesteps that without
            // affecting the rest of the (RTL) page.
            <Box sx={{ maxHeight: 480, overflowY: "auto", direction: "ltr" }}>
              <ResponsiveContainer width="100%" height={Math.max(220, labelPrintsPerUser.length * 36)}>
                <BarChart
                  data={labelPrintsPerUser}
                  layout="vertical"
                  margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
                >
                  <CartesianGrid horizontal={false} stroke={CHART_INK.grid} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: CHART_INK.muted }} axisLine={{ stroke: CHART_INK.axis }} tickLine={false} />
                  <YAxis
                    type="category"
                    dataKey="userName"
                    width={160}
                    tick={<UsernameAxisTick />}
                    axisLine={{ stroke: CHART_INK.axis }}
                    tickLine={false}
                  />
                  <Tooltip formatter={(value) => [`${value} מדבקות`, ""]} cursor={{ fill: alpha(LABEL_PRINTS_CHART_COLOR, 0.06) }} />
                  <Bar dataKey="count" name="מדבקות" fill={LABEL_PRINTS_CHART_COLOR} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Card>
      </Box>

      <Divider />

      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={1.5}>
          <Typography variant="h6" fontWeight={700}>קריאות אחרונות</Typography>
          <TextField
            size="small"
            label="חיפוש (מסמך / שם / טלפון)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>
        <Card sx={{ borderRadius: 3 }}>
          {loadingTickets ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress sx={{ color: PAGE_COLOR }} /></Box>
          ) : tickets.data.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>אין קריאות להצגה</Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>תאריך</TableCell>
                    <TableCell>מספר מסמך</TableCell>
                    <TableCell>שם</TableCell>
                    <TableCell>טלפון</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tickets.data.map((ticket) => (
                    <TableRow key={ticket._id} hover>
                      <TableCell>{new Date(ticket.createdAt).toLocaleString("he-IL")}</TableCell>
                      <TableCell>{ticket.docnum || "—"}</TableCell>
                      <TableCell>{ticket.nameform || "—"}</TableCell>
                      <TableCell>{ticket.phoneform || ticket.phone || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
        {tickets.truncated && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            מוצגות {tickets.data.length} מתוך {tickets.total} קריאות — השתמשו בחיפוש לצמצום התוצאות
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default EldanDashboardPage;
